<template>
  <div class="mt-2 text-left lista-arquivos">
    <div class="d-flex">
      <v-card
        v-for="(arquivo, index) in arquivosFormatados"
        :key="index"
        class="cursor-pointer hover-card transition fill-height d-flex flex-column"
        @click="handleArquivoClick(arquivo)"
        flat
        outlined
        style="border-radius: 8px; overflow: hidden; max-width: 120px;"
      >
        <div
          v-if="!isImage(arquivo) && !isVideo(arquivo)"
          class="d-flex align-center justify-center pt-2 flex-grow-1"
          style="min-height: 80px; background-color: #f5f5f5"
        >
          <v-icon size="40" :color="corIcone(arquivo)">{{
            icone(arquivo)
          }}</v-icon>
        </div>

        <v-img
          v-else-if="isImage(arquivo)"
          :src="
            getPreviewUrl(arquivo) ||
            'https://img.icons8.com/fluency/240/image.png'
          "
          height="80"
          class="flex-grow-0"
          cover
          @error="handleImageError(arquivo)"
        >
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular
                indeterminate
                color="primary"
                size="24"
              ></v-progress-circular>
            </v-row>
          </template>
        </v-img>

        <div
          v-else-if="isVideo(arquivo)"
          class="video-thumbnail flex-grow-0"
          style="height: 80px"
        >
          <video
            :src="getPreviewUrl(arquivo) || ''"
            class="video-preview"
            preload="metadata"
          ></video>
          <div class="video-overlay">
            <v-icon size="28" color="white">mdi-play-circle</v-icon>
          </div>
        </div>

        <div
          class="caption px-2 py-1 text-center text-truncate flex-grow-0"
          style="
            font-size: 11px !important;
            white-space: normal;
            line-height: 1.2;
            word-break: break-all;
          "
          :title="arquivo.nome"
        >
          {{ arquivo.nome }}
        </div>
      </v-card>
    </div>

    <v-dialog v-model="dialogAberto" max-width="800">
      <v-card v-if="arquivoAtual">
        <v-card-title
          class="d-flex justify-space-between align-center px-4 py-3 subtitle-1"
          style="word-break: break-all"
        >
          <span class="text-truncate">{{ arquivoAtual.nome }}</span>
          <v-btn icon small @click="fecharDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text
          class="pa-0 d-flex justify-center"
          style="background-color: #000"
        >
          <div v-if="isVideo(arquivoAtual)" style="width: 100%">
            <div v-if="carregandoArquivo" class="loading-container">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
            </div>
            <video
              v-else
              :src="urlAtual"
              controls
              class="video-player"
              preload="metadata"
              @error="handleVideoError"
            >
              Seu navegador não suporta o elemento de vídeo.
            </video>
          </div>

          <div v-else-if="isImage(arquivoAtual)" style="width: 100%">
            <div v-if="carregandoArquivo" class="loading-container">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
            </div>
            <v-img
              v-else
              :src="urlAtual"
              max-height="70vh"
              contain
              @error="handleImageError(arquivoAtual)"
            />
          </div>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="downloadArquivo" outlined>
            Baixar <v-icon right>mdi-download</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import API from "@/api/API";

export default {
  name: "ListaArquivosInteracao",
  props: {
    arquivos: {
      type: Array, // Strings (S3 paths / keys) ou Objetos (no caso de ser local optimistically)
      required: true,
      default: () => [],
    },
    ticketId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    previewUrls: {},
    loadingUrls: new Set(),
    dialogAberto: false,
    arquivoAtual: null,
    urlAtual: "",
    carregandoArquivo: false,
  }),
  computed: {
    arquivosFormatados() {
      if (!this.arquivos) return [];
      return this.arquivos.map((arq) => {
        if (typeof arq === "string") {
          const partes = arq.split("/");
          return {
            path: arq,
            nome: partes[partes.length - 1],
          };
        }
        if (arq.path) {
          return {
            path: arq.path,
            nome: arq.nome || arq.name || arq.path.split("/").pop(),
          };
        }
        return {
          path: null,
          nome: arq.nome || arq.name,
          fileObject: arq,
        };
      });
    },
  },
  mounted() {
    this.carregarPreviews();
  },
  watch: {
    arquivos: {
      handler() {
        this.carregarPreviews();
      },
      deep: true,
    },
  },
  methods: {
    isImage(arquivo) {
      const ext = [".jpg", ".jpeg", ".png", ".gif", ".svg", ".webp", ".bmp"];
      const nome = (arquivo.nome || "").toLowerCase();
      return ext.some((e) => nome.endsWith(e));
    },
    isVideo(arquivo) {
      const ext = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];
      const nome = (arquivo.nome || "").toLowerCase();
      return ext.some((e) => nome.endsWith(e));
    },
    isPreviewSupported(arquivo) {
      return this.isImage(arquivo) || this.isVideo(arquivo);
    },
    getFileName(path) {
      const id = this.ticketId.includes("_")
        ? this.ticketId.split("_")[1]
        : this.ticketId;
      return path.replace(`${id}/`, "");
    },
    getPreviewUrl(arquivo) {
      if (!arquivo.path && arquivo.fileObject) {
        if (!this.previewUrls[arquivo.nome]) {
          this.previewUrls = {
            ...this.previewUrls,
            [arquivo.nome]: URL.createObjectURL(arquivo.fileObject),
          };
        }
        return this.previewUrls[arquivo.nome];
      }

      const path = arquivo.path;
      if (this.previewUrls[path]) {
        return this.previewUrls[path];
      }

      if (!this.loadingUrls.has(path)) {
        this.loadPreviewUrl(arquivo);
      }

      return null;
    },
    async loadPreviewUrl(arquivo) {
      if (!arquivo.path) return;

      const path = arquivo.path;
      this.loadingUrls.add(path);
      try {
        const url = await API.getArquivoSignedUrl(
          this.ticketId,
          this.getFileName(path),
        );
        this.previewUrls = { ...this.previewUrls, [path]: url };
      } catch (error) {
        console.error("Erro ao carregar preview S3:", error);
      } finally {
        this.loadingUrls.delete(path);
      }
    },
    handleImageError(arquivo) {
      const path = arquivo.path || arquivo.nome;
      this.previewUrls = {
        ...this.previewUrls,
        [path]: "https://img.icons8.com/fluency/240/image.png",
      };
    },
    handleVideoError() {
      this.carregandoArquivo = false;
    },
    async handleArquivoClick(arquivo) {
      if (this.isPreviewSupported(arquivo)) {
        await this.abrirPreview(arquivo);
      } else {
        this.download(arquivo);
      }
    },
    async abrirPreview(arquivo) {
      this.carregandoArquivo = true;
      this.arquivoAtual = arquivo;
      this.dialogAberto = true;

      if (!arquivo.path && arquivo.fileObject) {
        this.urlAtual = URL.createObjectURL(arquivo.fileObject);
        this.carregandoArquivo = false;
        return;
      }

      let url = this.previewUrls[arquivo.path];
      if (!url) {
        try {
          url = await API.getArquivoSignedUrl(
            this.ticketId,
            this.getFileName(arquivo.path),
          );
          this.previewUrls = { ...this.previewUrls, [arquivo.path]: url };
        } catch (e) {
          console.log("Erro ao obter URL do arquivo para preview:", e);
        }
      }

      this.urlAtual = url;
      this.carregandoArquivo = false;
    },
    download(arquivo) {
      const url = arquivo.path
        ? this.previewUrls[arquivo.path]
        : arquivo.fileObject
        ? URL.createObjectURL(arquivo.fileObject)
        : null;
      if (url) {
        this.simularDownload(arquivo.nome, url);
      } else if (arquivo.path) {
        API.getArquivoSignedUrl(
          this.ticketId,
          this.getFileName(arquivo.path),
        ).then((u) => {
          this.simularDownload(arquivo.nome, u);
        });
      }
    },
    downloadArquivo() {
      if (this.arquivoAtual) {
        this.download(this.arquivoAtual);
      }
    },
    simularDownload(name, url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    fecharDialog() {
      this.dialogAberto = false;
      this.arquivoAtual = null;
      this.urlAtual = "";
      this.carregandoArquivo = false;
    },
    carregarPreviews() {
      this.arquivosFormatados.forEach((arquivo) => {
        if (this.isPreviewSupported(arquivo)) {
          this.getPreviewUrl(arquivo);
        }
      });
    },
    icone(arquivo) {
      const nome = (arquivo.nome || "").toLowerCase();
      if (/\.pdf$/.test(nome)) return "mdi-file-pdf-box";
      if (/\.(doc|docx)$/.test(nome)) return "mdi-file-word";
      if (/\.(xls|xlsx)$/.test(nome)) return "mdi-file-excel";
      if (/\.zip$/.test(nome)) return "mdi-folder-zip";
      return "mdi-file";
    },
    corIcone(arquivo) {
      const nome = (arquivo.nome || "").toLowerCase();
      if (/\.pdf$/.test(nome)) return "#C62828";
      if (/\.(doc|docx)$/.test(nome)) return "#1565C0";
      if (/\.(xls|xlsx)$/.test(nome)) return "#2E7D32";
      return "#616161";
    },
  },
};
</script>

<style scoped>
.hover-card {
  transition: box-shadow 0.2s, transform 0.2s;
}
.hover-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.video-thumbnail {
  position: relative;
  width: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
}

.video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  transition: background 0.3s ease;
}

.video-thumbnail:hover .video-overlay {
  background: rgba(0, 0, 0, 0.5);
}

.video-player {
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  width: 100%;
}
</style>
